// CertificationItem.jsx
import { useState, useCallback, useEffect, useRef } from 'react';
import CloseTooltipIcon from '../icons/CloseToolTipIcon';
import RightArrowIcon from '../icons/RightArrowIcon';

export default function CertificationItem({ text }) {
  const [open, setOpen] = useState(false);
  const [manuallyClosed, setManuallyClosed] = useState(false);
  const rootRef = useRef(null);
  const tipRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, arrowX: 16 });

  const limit = 9;
  const isTruncated = text?.length > limit;
  const visible = isTruncated ? text.substring(0, limit) + '...' : text;

  const getCardEl = () => rootRef.current?.closest('.card');
  const getLabelEl = () => rootRef.current?.querySelector('.cert-label');

  useEffect(() => {
    const card = getCardEl();
    if (!card) return;
    if (open) card.classList.add('card--tooltip-open');
    else card.classList.remove('card--tooltip-open');
    return () => card.classList.remove('card--tooltip-open');
  }, [open]);

  const recalcAfterOpen = (clickX, clickY) => {
    const card = getCardEl();
    const tip = tipRef.current;
    if (!card || !tip) return;

    const pad = 24;
    const cardRect = card.getBoundingClientRect();
    const cardW = card.clientWidth;
    const cardH = card.clientHeight;

    const tipW = tip.offsetWidth;
    const tipH = tip.offsetHeight;

    let left = clickX - cardRect.left - Math.round(tipW * 0.33);
    let top = clickY - cardRect.top - Math.round(tipH * 0.5);

    left = Math.max(pad, Math.min(left, cardW - tipW - pad));
    top = Math.max(pad, Math.min(top, cardH - tipH - pad));

    const arrowX = Math.max(
      12,
      Math.min(clickX - cardRect.left - left, tipW - 12)
    );

    setPos({ left, top, arrowX });
  };

  const openTooltip = useCallback(
    (e) => {
      e.stopPropagation();
      if (!isTruncated) return;
      setManuallyClosed(false);
      setOpen(true);

      requestAnimationFrame(() => recalcAfterOpen(e.clientX, e.clientY));
    },
    [isTruncated]
  );

  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      const lbl = getLabelEl();
      if (!lbl) return;
      const rect = lbl.getBoundingClientRect();
      recalcAfterOpen(rect.left + rect.width / 2, rect.top + rect.height / 2);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  const closeTooltip = useCallback((e) => {
    e?.stopPropagation();
    setOpen(false);
    setManuallyClosed(true);
  }, []);

  const onMouseLeave = () => {
    if (open && !manuallyClosed) setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!isTruncated) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openTooltip(e);
    } else if (e.key === 'Escape') {
      closeTooltip(e);
    }
  };

  return (
    <div className="certifications" ref={rootRef} onMouseLeave={onMouseLeave}>
      <img alt="icon" src="/images/checked-circle-white.svg" />
      <p
        className={`cert-label ${isTruncated ? 'is-truncated' : ''}`}
        role={isTruncated ? 'button' : undefined}
        tabIndex={isTruncated ? 0 : -1}
        aria-expanded={isTruncated ? open : undefined}
        onClick={openTooltip}
        onKeyDown={onKeyDown}
      >
        {visible}
      </p>
      {isTruncated ? <RightArrowIcon /> : null}
      {isTruncated && open && (
        <div
          ref={tipRef}
          className="cert-tooltip"
          style={{
            left: `${pos.left}px`,
            top: `${pos.top}px`,
            '--arrow-x': `${pos.arrowX}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <img alt="icon" src="/images/checked-circle-white.svg" />
          <div className="cert-tooltip__content">{text}</div>
          <button
            className="cert-tooltip__close"
            aria-label="Close"
            onClick={closeTooltip}
          >
            <CloseTooltipIcon />
          </button>
        </div>
      )}
    </div>
  );
}
